"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
// Shorthands
var min = Math.min, max = Math.max, floor = Math.floor, round = Math.round;
/**
 * Tries to convert a color name to rgb/a hex representation
 * @param name
 * @returns {string | CanvasGradient | CanvasPattern}
 */
function standardizeColor(name) {
    var ctx = document.createElement('canvas').getContext('2d');
    ctx.fillStyle = name;
    return ctx.fillStyle;
}
/**
 * Convert HSV spectrum to RGB.
 * @param h Hue
 * @param s Saturation
 * @param v Value
 * @returns {number[]} Array with rgb values.
 */
function hsvToRgb(h, s, v) {
    h = (h / 360) * 6;
    s /= 100;
    v /= 100;
    var i = floor(h);
    var f = h - i;
    var p = v * (1 - s);
    var q = v * (1 - f * s);
    var t = v * (1 - (1 - f) * s);
    var mod = i % 6;
    var r = [v, q, p, p, t, v][mod];
    var g = [t, v, v, q, p, p][mod];
    var b = [p, p, t, v, v, q][mod];
    return [
        r * 255,
        g * 255,
        b * 255
    ];
}
exports.hsvToRgb = hsvToRgb;
/**
 * Convert HSV spectrum to Hex.
 * @param h Hue
 * @param s Saturation
 * @param v Value
 * @returns {string[]} Hex values
 */
function hsvToHex(h, s, v) {
    return hsvToRgb(h, s, v).map(function (v) {
        return round(v).toString(16).padStart(2, '0');
    });
}
exports.hsvToHex = hsvToHex;
/**
 * Convert HSV spectrum to CMYK.
 * @param h Hue
 * @param s Saturation
 * @param v Value
 * @returns {number[]} CMYK values
 */
function hsvToCmyk(h, s, v) {
    var rgb = hsvToRgb(h, s, v);
    var r = rgb[0] / 255;
    var g = rgb[1] / 255;
    var b = rgb[2] / 255;
    var k, c, m, y;
    k = min(1 - r, 1 - g, 1 - b);
    c = k === 1 ? 0 : (1 - r - k) / (1 - k);
    m = k === 1 ? 0 : (1 - g - k) / (1 - k);
    y = k === 1 ? 0 : (1 - b - k) / (1 - k);
    return [
        c * 100,
        m * 100,
        y * 100,
        k * 100
    ];
}
exports.hsvToCmyk = hsvToCmyk;
/**
 * Convert HSV spectrum to HSL.
 * @param h Hue
 * @param s Saturation
 * @param v Value
 * @returns {number[]} HSL values
 */
function hsvToHsl(h, s, v) {
    s /= 100, v /= 100;
    var l = (2 - s) * v / 2;
    if (l !== 0) {
        if (l === 1) {
            s = 0;
        }
        else if (l < 0.5) {
            s = s * v / (l * 2);
        }
        else {
            s = s * v / (2 - l * 2);
        }
    }
    return [
        h,
        s * 100,
        l * 100
    ];
}
exports.hsvToHsl = hsvToHsl;
/**
 * Convert RGB to HSV.
 * @param r Red
 * @param g Green
 * @param b Blue
 * @return {number[]} HSV values.
 */
function rgbToHsv(r, g, b) {
    r /= 255, g /= 255, b /= 255;
    var h, s, v;
    var minVal = min(r, g, b);
    var maxVal = max(r, g, b);
    var delta = maxVal - minVal;
    v = maxVal;
    if (delta === 0) {
        h = s = 0;
    }
    else {
        s = delta / maxVal;
        var dr = (((maxVal - r) / 6) + (delta / 2)) / delta;
        var dg = (((maxVal - g) / 6) + (delta / 2)) / delta;
        var db = (((maxVal - b) / 6) + (delta / 2)) / delta;
        if (r === maxVal) {
            h = db - dg;
        }
        else if (g === maxVal) {
            h = (1 / 3) + dr - db;
        }
        else if (b === maxVal) {
            h = (2 / 3) + dg - dr;
        }
        if (h < 0) {
            h += 1;
        }
        else if (h > 1) {
            h -= 1;
        }
    }
    return [
        h * 360,
        s * 100,
        v * 100
    ];
}
/**
 * Convert CMYK to HSV.
 * @param c Cyan
 * @param m Magenta
 * @param y Yellow
 * @param k Key (Black)
 * @return {number[]} HSV values.
 */
function cmykToHsv(c, m, y, k) {
    c /= 100, m /= 100, y /= 100, k /= 100;
    var r = (1 - min(1, c * (1 - k) + k)) * 255;
    var g = (1 - min(1, m * (1 - k) + k)) * 255;
    var b = (1 - min(1, y * (1 - k) + k)) * 255;
    return rgbToHsv(r, g, b).slice();
}
/**
 * Convert HSL to HSV.
 * @param h Hue
 * @param s Saturation
 * @param l Lightness
 * @return {number[]} HSV values.
 */
function hslToHsv(h, s, l) {
    s /= 100, l /= 100;
    s *= l < 0.5 ? l : 1 - l;
    var ns = (2 * s / (l + s)) * 100;
    var v = (l + s) * 100;
    return [h, ns, v];
}
/**
 * Convert HEX to HSV.
 * @param hex Hexadecimal string of rgb colors, can have length 3 or 6.
 * @return {number[]} HSV values.
 */
function hexToHsv(hex) {
    return rgbToHsv.apply(void 0, hex.match(/.{2}/g).map(function (v) { return parseInt(v, 16); }));
}
/**
 * Try's to parse a string which represents a color to a HSV array.
 * Current supported types are cmyk, rgba, hsla and hexadecimal.
 * @param str
 * @return {*}
 */
function parseToHSV(str) {
    var _a, _b;
    // Check if string is a color-name
    str = str.match(/^[a-zA-Z]+$/) ? standardizeColor(str) : str;
    // Regular expressions to match different types of color represention
    var regex = {
        cmyk: /^cmyk[\D]+(\d+)[\D]+(\d+)[\D]+(\d+)[\D]+(\d+)/i,
        rgba: /^(rgb|rgba)[\D]+(\d+)[\D]+(\d+)[\D]+(\d+)[\D]*?([\d.]+|$)/i,
        hsla: /^(hsl|hsla)[\D]+(\d+)[\D]+(\d+)[\D]+(\d+)[\D]*?([\d.]+|$)/i,
        hsva: /^(hsv|hsva)[\D]+(\d+)[\D]+(\d+)[\D]+(\d+)[\D]*?([\d.]+|$)/i,
        hex: /^#?(([\dA-Fa-f]{3,4})|([\dA-Fa-f]{6})|([\dA-Fa-f]{8}))$/i
    };
    /**
     * Takes an Array of any type, convert strings which represents
     * a number to a number an anything else to undefined.
     * @param array
     * @return {*}
     */
    var numarize = function (array) { return array.map(function (v) { return /^(|\d+)\.\d+|\d+$/.test(v) ? Number(v) : undefined; }); };
    var match;
    for (var type in regex) {
        // Check if current scheme passed
        if (!(match = regex[type].exec(str)))
            continue;
        // Try to convert
        switch (type) {
            case 'cmyk': {
                var _c = numarize(match), c = _c[1], m = _c[2], y = _c[3], k = _c[4];
                if (c > 100 || m > 100 || y > 100 || k > 100)
                    break;
                return { values: cmykToHsv(c, m, y, k).concat([1]), type: type };
            }
            case 'rgba': {
                var _d = numarize(match), r = _d[2], g = _d[3], b = _d[4], _e = _d[5], a = _e === void 0 ? 1 : _e;
                if (r > 255 || g > 255 || b > 255 || a < 0 || a > 1)
                    break;
                return { values: rgbToHsv(r, g, b).concat([a]), type: type };
            }
            case 'hex': {
                var splitAt = function (s, i) { return [s.substring(0, i), s.substring(i, s.length)]; };
                var hex = match[1];
                // Fill up opacity if not declared
                if (hex.length === 3) {
                    hex += 'F';
                }
                else if (hex.length === 6) {
                    hex += 'FF';
                }
                var alpha = void 0;
                if (hex.length === 4) {
                    _a = splitAt(hex, 3).map(function (v) { return v + v; }), hex = _a[0], alpha = _a[1];
                }
                else if (hex.length === 8) {
                    _b = splitAt(hex, 6), hex = _b[0], alpha = _b[1];
                }
                // Convert 0 - 255 to 0 - 1 for opacity
                alpha = parseInt(alpha, 16) / 255;
                return { values: hexToHsv(hex).concat([alpha]), type: type };
            }
            case 'hsla': {
                var _f = numarize(match), h = _f[2], s = _f[3], l = _f[4], _g = _f[5], a = _g === void 0 ? 1 : _g;
                if (h > 360 || s > 100 || l > 100 || a < 0 || a > 1)
                    break;
                return { values: hslToHsv(h, s, l).concat([a]), type: type };
            }
            case 'hsva': {
                var _h = numarize(match), h = _h[2], s = _h[3], v = _h[4], _j = _h[5], a = _j === void 0 ? 1 : _j;
                if (h > 360 || s > 100 || v > 100 || a < 0 || a > 1)
                    break;
                return { values: [h, s, v, a], type: type };
            }
        }
    }
    return { values: null, type: null };
}
exports.parseToHSV = parseToHSV;
